/**
 * InteractiveSessionProvider — React context provider that listens for
 * interactive action sessions and renders modal dialogs.
 *
 * Mount this provider near the root of the app. When an interactive action
 * is invoked, this component receives the session event, renders the
 * appropriate UI (modal, panel, etc.), and resolves the session when the
 * user completes the interaction.
 *
 * The provider uses no plugin-specific logic — it renders a generic
 * markdown editor modal for any action with `interaction: 'modal'` that
 * operates on text content. Plugins can register custom UI components
 * via the action registry's `uiComponent` field for richer experiences.
 *
 * Wave 23.2 — Interactive Capability Pipeline Fix
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type {
  InteractiveSession,
  InteractiveModalResult,
} from './interactive-action-session';
import {
  onInteractiveSession,
  getInteractionMode,
} from './interactive-action-session';
import { useActionRegistryStore } from './registries/action-registry';

// ── Types ──────────────────────────────────────────────────

interface ActiveModal {
  session: InteractiveSession<InteractiveModalResult>;
  editedContent: string;
}

// ── Styles (DS 2.0 tokens) ────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: 'var(--nc-space-6, 24px)',
};

const dialogStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '720px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 'var(--nc-radius-lg, 12px)',
  boxShadow: 'var(--nc-shadow-lg, 0 8px 32px rgba(0,0,0,0.2))',
  overflow: 'hidden',
  backgroundColor: 'var(--nc-color-surface, #fff)',
  border: '1px solid var(--nc-color-border, #e2e8f0)',
};

const headerStyle: React.CSSProperties = {
  padding: 'var(--nc-space-4, 16px)',
  borderBottom: '1px solid var(--nc-color-border, #e2e8f0)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  padding: 'var(--nc-space-4, 16px)',
  overflow: 'auto',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '300px',
  maxHeight: '50vh',
  resize: 'vertical',
  fontFamily: 'var(--nc-font-mono, monospace)',
  fontSize: 'var(--nc-font-size-sm, 13px)',
  lineHeight: 'var(--nc-line-height-relaxed, 1.6)',
  padding: 'var(--nc-space-3, 12px)',
  borderRadius: 'var(--nc-radius-md, 8px)',
  border: '1px solid var(--nc-color-border, #e2e8f0)',
  backgroundColor: 'var(--nc-color-bg-subtle, #fafafa)',
  color: 'var(--nc-color-text, #1a202c)',
  outline: 'none',
};

const footerStyle: React.CSSProperties = {
  padding: 'var(--nc-space-3, 12px) var(--nc-space-4, 16px)',
  borderTop: '1px solid var(--nc-color-border, #e2e8f0)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 'var(--nc-space-3, 12px)',
};

const buttonBaseStyle: React.CSSProperties = {
  padding: 'var(--nc-space-2, 8px) var(--nc-space-4, 16px)',
  borderRadius: 'var(--nc-radius-md, 8px)',
  fontSize: 'var(--nc-font-size-sm, 13px)',
  fontWeight: 500,
  cursor: 'pointer',
  border: '1px solid var(--nc-color-border, #e2e8f0)',
  transition: 'opacity 0.15s',
};

const applyButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: 'var(--nc-color-primary, #4F46E5)',
  color: '#fff',
  borderColor: 'var(--nc-color-primary, #4F46E5)',
};

const cancelButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  backgroundColor: 'transparent',
  color: 'var(--nc-color-text, #1a202c)',
};

const metaStyle: React.CSSProperties = {
  fontSize: 'var(--nc-font-size-xs, 11px)',
  color: 'var(--nc-color-text-muted, #718096)',
};

// ── Provider Component ─────────────────────────────────────

export function InteractiveSessionProvider({ children }: { children: React.ReactNode }) {
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const getUIComponent = useActionRegistryStore((s) => s.getUIComponent);

  // Subscribe to interactive session events
  useEffect(() => {
    const unsubscribe = onInteractiveSession((session) => {
      const mode = getInteractionMode(session.action);

      if (mode === 'modal') {
        const initialContent =
          typeof session.inputContent === 'string'
            ? session.inputContent
            : JSON.stringify(session.inputContent, null, 2);

        setActiveModal({
          session,
          editedContent: initialContent,
        });
      } else {
        // For now, unsupported interaction modes are rejected
        session.reject(
          `Interaction mode '${mode}' is not yet supported by the UI session provider`,
        );
      }
    });

    return unsubscribe;
  }, []);

  // Focus textarea when modal opens
  useEffect(() => {
    if (activeModal && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [activeModal]);

  const handleApply = useCallback(() => {
    if (!activeModal) return;
    activeModal.session.resolve({
      content: activeModal.editedContent,
      applied: true,
      metadata: {
        interactionMode: getInteractionMode(activeModal.session.action),
        editedAt: new Date().toISOString(),
      },
    });
    setActiveModal(null);
  }, [activeModal]);

  const handleCancel = useCallback(() => {
    if (!activeModal) return;
    activeModal.session.reject('User cancelled');
    setActiveModal(null);
  }, [activeModal]);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setActiveModal((prev) =>
      prev ? { ...prev, editedContent: e.target.value } : null,
    );
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Ctrl/Cmd + Enter → Apply
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleApply();
      }
      // Escape → Cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleApply, handleCancel],
  );

  // Compute change delta
  const originalContent = activeModal
    ? typeof activeModal.session.inputContent === 'string'
      ? activeModal.session.inputContent
      : JSON.stringify(activeModal.session.inputContent, null, 2)
    : '';
  const hasChanges = activeModal ? activeModal.editedContent !== originalContent : false;
  const charDelta = activeModal ? activeModal.editedContent.length - originalContent.length : 0;
  const deltaLabel =
    charDelta === 0
      ? 'no changes'
      : charDelta > 0
        ? `+${charDelta} chars`
        : `${charDelta} chars`;

  // Check for custom UI component
  const CustomComponent = activeModal?.session.action.uiComponent
    ? getUIComponent(activeModal.session.action.uiComponent)
    : undefined;

  return (
    <>
      {children}

      {/* Interactive Modal Overlay */}
      {activeModal && (
        <div
          style={overlayStyle}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancel();
          }}
          role="dialog"
          aria-modal="true"
          aria-label={`${activeModal.session.action.label} — Interactive Session`}
        >
          <div style={dialogStyle} onKeyDown={handleKeyDown}>
            {/* Header */}
            <div style={headerStyle}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--nc-font-size-md, 14px)' }}>
                  {activeModal.session.action.icon ?? '✏️'}{' '}
                  {activeModal.session.action.label}
                </div>
                <div style={metaStyle}>
                  {activeModal.session.action.description}
                </div>
              </div>
              <button
                onClick={handleCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: 'var(--nc-color-text-muted, #718096)',
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={bodyStyle}>
              {/* Context info */}
              <div style={{ ...metaStyle, marginBottom: 'var(--nc-space-3, 12px)', display: 'flex', gap: 'var(--nc-space-4, 16px)' }}>
                {activeModal.session.context?.sourcePluginId && (
                  <span>
                    Source: <strong>{activeModal.session.context.sourcePluginId}</strong>
                  </span>
                )}
                {activeModal.session.context?.entityId && (
                  <span>
                    Entity: <strong>{activeModal.session.context.entityId}</strong>
                  </span>
                )}
                <span>
                  {originalContent.length} chars · {originalContent.split('\n').length} lines
                </span>
              </div>

              {/* Custom or generic editor */}
              {CustomComponent ? (
                <CustomComponent
                  content={activeModal.editedContent}
                  originalContent={originalContent}
                  action={activeModal.session.action}
                  context={activeModal.session.context}
                  metadata={activeModal.session.metadata}
                  onChange={(content: string) =>
                    setActiveModal((prev) =>
                      prev ? { ...prev, editedContent: content } : null,
                    )
                  }
                  onApply={handleApply}
                  onCancel={handleCancel}
                />
              ) : (
                <textarea
                  ref={textareaRef}
                  style={textareaStyle}
                  value={activeModal.editedContent}
                  onChange={handleContentChange}
                  spellCheck={false}
                  aria-label="Edit content"
                />
              )}
            </div>

            {/* Footer */}
            {!CustomComponent && (
              <div style={footerStyle}>
                <span style={metaStyle}>
                  {hasChanges ? deltaLabel : 'No changes yet'} · Ctrl+Enter to apply
                </span>
                <div style={{ display: 'flex', gap: 'var(--nc-space-2, 8px)' }}>
                  <button style={cancelButtonStyle} onClick={handleCancel}>
                    Cancel
                  </button>
                  <button
                    style={{
                      ...applyButtonStyle,
                      opacity: hasChanges ? 1 : 0.5,
                      cursor: hasChanges ? 'pointer' : 'not-allowed',
                    }}
                    onClick={handleApply}
                    disabled={!hasChanges}
                  >
                    Apply Changes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
